import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SaviBusinessJsonLd from "../../components/SaviBusinessJsonLd";
import SaviQuiz from "../../components/SaviQuiz";
import { ArrowRight, Volume2, VolumeX, Moon, Coffee, Mountain, Wifi } from "lucide-react";
import { pic } from "../../lib/pic";

const QuietColivingValenciaPage = () => {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Quiet Coliving Valencia | Savi — Focus Without the Noise</title>
        <meta name="description" content="Looking for a quiet coliving in Valencia? Savi is a sober, distraction-free villa in the mountains. No parties, no noise. Just focused people doing real work." />
        <link rel="canonical" href="https://www.savicoliving.com/quiet-coliving-valencia" />
        <meta property="og:title" content="Quiet Coliving Valencia | Savi Coliving" />
        <meta property="og:description" content="A sober, distraction-free coliving in the mountains near Valencia. For people who need to focus." />
      </Helmet>
      <SaviBusinessJsonLd id="quiet-coliving-valencia" />

      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FBF7F1]/95 backdrop-blur-lg border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" alt="Savi" className="h-7 w-auto" />
            <span className="text-base tracking-wider text-[#C89B7B]"><span style={{fontFamily: 'Cinzel, serif'}}>SAVI</span> <span style={{fontFamily: "'Cormorant Garamond', serif"}}>Coliving</span></span>
          </Link>
          <Link to="/" className="text-sm text-[#C85A38] font-medium hover:underline">View the house →</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-14">
        <div className="relative h-[70vh] overflow-hidden">
          <img {...pic("mountain-view")} fetchpriority="high" decoding="async" alt="Quiet mountain villa near Valencia" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2E2A26] via-[#2E2A26]/50 to-[#2E2A26]/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4 max-w-4xl leading-tight" style={{textShadow: '0 2px 12px rgba(0,0,0,0.5)'}}>
              Finally. A quiet coliving<br className="hidden sm:block" /> near Valencia.
            </h1>
            <p className="text-white/60 text-base sm:text-lg max-w-xl mb-8" style={{textShadow: '0 2px 8px rgba(0,0,0,0.4)'}}>
              No bass through the walls at 2am. No "community drinks." Just a calm house in the mountains where people actually work.
            </p>
            <Link to="/">
              <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-4 text-sm font-medium tracking-wide transition-all">
                Check availability <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-5">The noise problem with most colivings</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            You searched for "<strong className="text-[#2E2A26]/80">quiet coliving Valencia</strong>" for a reason. Maybe you've been in a shared flat where someone's on a Zoom call in the kitchen while someone else is playing music in the living room. Maybe you tried a coliving that was "community-focused" — which turned out to mean loud.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            I get it. I lived that life for years before building Savi. The problem isn't shared living — it's who you're sharing with and what the culture is.
          </p>
        </div>
      </section>

      {/* THE DIFFERENCE */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">What makes Savi quiet</h2>
          <div className="space-y-4">
            {[
              { icon: VolumeX, title: "100% sober", text: "No alcohol, no recreational drugs. This alone changes the energy of a house more than anything else. Evenings are calm. Mornings are productive." },
              { icon: Moon, title: "Quiet hours respected", text: "10pm-8am. Not enforced with rules — just naturally respected because everyone here actually wants to sleep and wake up clearheaded." },
              { icon: Mountain, title: "Mountain setting", text: "No street noise. No traffic. No nightclub down the road. Just birds, wind, and the occasional dog barking. The Sierra Calderona is genuinely peaceful." },
              { icon: Coffee, title: "Small group", text: "up to 16 people. You know everyone. There's no anonymous rotation of short-stay guests. People stay 1-3 months and actually invest in the space." }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#C85A38]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <p className="text-[#2E2A26] text-sm font-medium mb-1">{item.title}</p>
                  <p className="text-[#2E2A26]/60 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-5">Quiet doesn't mean boring</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            There's a difference between quiet and dead. Savi is deeply alive — it's just the right kind of alive. Real conversations over dinner. Skill shares where people teach each other what they know. Group accountability sessions. Hikes in the sierra. Pool sessions. Gym. Cooking together.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            The <strong className="text-[#2E2A26]/80">quiet coliving</strong> thing isn't about silence. It's about removing the noise that doesn't serve you — the hangovers, the drama, the distraction — so the good stuff has room to happen.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            Starlink WiFi (up to 500Mbps), a proper coworking space with dual screens, a roof terrace with mountain views, and everything you need to live and work well. All included in the monthly rate.
          </p>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-2xl mx-auto">
          <blockquote className="border-l-2 border-[#C85A38] pl-5">
            <p className="text-[#2E2A26]/70 text-base leading-relaxed font-serif italic mb-3">
              "I've come across many co-working and co-living spaces through my work — Savi, with its sober concept, is truly something very special. A place that leaves a lasting impression."
            </p>
            <div className="flex items-center gap-2.5">
              <img src="https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/wr0ogbx9_saskia.jpeg" alt="Saskia Rehhahn" className="w-8 h-8 rounded-full object-cover" loading="lazy" />
              <p className="text-[#2E2A26]/90 text-xs font-medium">Saskia Rehhahn <span className="text-[#2E2A26]/40">— Hospitality professional</span></p>
            </div>
          </blockquote>
        </div>
      </section>

      {/* QUIZ */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-xl mx-auto text-center mb-8">
          <p className="text-[#C85A38] text-xs font-medium tracking-[0.2em] uppercase mb-2">30 seconds</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-2">Is Savi right for you?</h2>
          <p className="text-[#2E2A26]/50 text-sm">Four quick questions. No sign-up required.</p>
        </div>
        <SaviQuiz />
      </section>

      {/* CTA */}
      <section className="py-14 px-4 bg-[#2E2A26]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">Ready for some quiet?</h2>
          <p className="text-white/50 text-sm mb-8 max-w-md mx-auto">
            Rooms from €1,200/month. Everything included. We review every application to keep the energy right.
          </p>
          <Link to="/">
            <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-12 py-5 text-sm font-medium transition-all">
              View Savi Coliving <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-8 px-4 bg-[#FBF7F1]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#2E2A26]/30 text-xs uppercase tracking-wider mb-3">Related</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/luxury-coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Luxury Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/coliving-valencia-for-founders" className="text-[#C85A38] text-xs hover:underline">Coliving for Founders</Link>
          </div>
        </div>
      </section>

      <footer className="bg-white py-6 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#2E2A26]/40 text-xs">&copy; 2026 SAVI Coliving</p>
          <div className="flex gap-4 text-xs text-[#2E2A26]/40">
            <Link to="/privacy" className="hover:text-[#2E2A26]">Privacy</Link>
            <Link to="/cookies" className="hover:text-[#2E2A26]">Cookies</Link>
            <Link to="/" className="hover:text-[#2E2A26]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default QuietColivingValenciaPage;
